import { useCallback, useEffect, useState } from "react";

import safeStorage from "../lib/safeStorage";

const STORAGE_KEY = "xpnsr_theme";

const readInitial = () => {
  if (typeof document === "undefined") return "dark";
  const stored = safeStorage.getItem(STORAGE_KEY);
  if (stored === "dark" || stored === "light") return stored;
  return document.documentElement.getAttribute("data-theme") || "dark";
};

/**
 * Single source of truth for the in-app theme. Mirrors `<html data-theme>`
 * and `localStorage.xpnsr_theme`. Multiple consumers stay in sync via a
 * MutationObserver on the documentElement attribute, so toggling in one
 * spot updates every other consumer (TopBar, Sidebar, charts) at once.
 */
export default function useTheme() {
  const [theme, setTheme] = useState(readInitial);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    safeStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    const sync = () => {
      const next = document.documentElement.getAttribute("data-theme");
      if (next === "dark" || next === "light") {
        setTheme((prev) => (prev === next ? prev : next));
      }
    };
    const obs = new MutationObserver(sync);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => obs.disconnect();
  }, []);

  const toggle = useCallback(() => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  return { theme, toggle };
}
